import InvoicePreview from '../Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUnlink, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { Autocomplete, TextField } from '@mui/material';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { inject } from 'mobx-react';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './invoice-preview-select.scss';

const InvoicePreviewSelect = ({ data, transaction, statement, updateTransaction, matchingExpenses, removeExpense, additionalExpenseOptions }) => {
  const expenses = [
    ...(statement.expenses || []),
    ...(additionalExpenseOptions && Array.isArray(additionalExpenseOptions) ? additionalExpenseOptions : []),
  ];

  const matchedExpense = statement.expenses?.find((expense) => expense.checksum === transaction.checksum);
  const receiptLabel = matchedExpense?.original_file_name || matchedExpense?.name || matchedExpense?.json?.Name || transaction.json?.Name || transaction.name || '—';

  // Keep track of previously matched transaction so we don't flash a spinner if processing new receipts
  const lastMatchedRef = useRef(null);

  useEffect(() => {
    if (transaction?.checksum) {
      lastMatchedRef.current = {
        checksum: transaction.checksum,
        file_name: transaction.file_name ? transaction.file_name : `${statement._id}_${transaction.checksum}.pdf`,
        name: transaction.name,
      };
    }
  }, [transaction?.checksum, transaction?.file_name, transaction?.name, statement?._id]);

  const handleChange = (choice) => {
    if (!choice) return;
    const selectedExpense = statement.expenses?.find((xp) => xp.checksum === choice.value);
    if (selectedExpense) {
      updateTransaction({
        ...transaction,
        json: selectedExpense.json,
        checksum: choice.value,
        name: choice.value,
      });
    }

    const selectedCategory = additionalExpenseOptions?.find((xp) => xp.checksum === choice.value);
    if (selectedCategory) {
      const supplier = selectedCategory.supplier;
      const taxesPercentage = data.config?.tax_profile?.reduce((acc, t) => {
        acc[t.code] = t.adjusted_rates ?? t.list_rates;
        return acc;
      }, {});
      const listItems = [
        {
          IsBillable: supplier?.IsBillable,
          Total: transaction.amount,
          Tax1: 0,
          Tax2: 0,
          GLNatural: supplier?.gl_code,
          CostType: supplier?.cost_type || '',
          // gl_office is the GL-office space, office is the office space (XPEN-3115).
          GLOffice: supplier?.gl_office || supplier?.office,
          Office: supplier?.office,
          Description: selectedCategory.name,
        },
      ];

      const InvoiceDate = new Date(transaction.date).toISOString().split('T')[0];
      const DueDate =
        Number.isInteger(supplier.payment_terms) && InvoiceDate
          ? new Date(new Date(InvoiceDate).setDate(new Date(InvoiceDate).getDate() + supplier.payment_terms)).toISOString().split('T')[0]
          : '';
      const json = {
        from_category: true,
        Status: 'Completed',
        InvoiceDate: InvoiceDate,
        DueDate: DueDate,
        GLNatural: supplier?.gl_code,
        CostType: supplier?.cost_type || '',
        IsBillable: supplier?.IsBillable,
        Office: supplier?.office,
        // No office fallback here: the header is GLValue-space and the export
        // reads it raw, so an office code would skip the Office -> GLValue step.
        GLOffice: supplier?.gl_office,
        Currency: statement.currency_code, 
          Category: selectedCategory.name,
          Name: selectedCategory.name,
        ListItem: listItems,
        Tax1: 0,
        Tax2: 0,
        Total: transaction.amount,
        Tax: 0,
        TotalWithTax: transaction.amount,
        BigTotalPayed: transaction.amount,
        ListSupplier: [
          {
            Name: supplier?.names[0],
            Number: supplier?.supplier_number,
            Index: supplier?._id,
            GLNatural: supplier?.gl_code,
            CostType: supplier?.cost_type || '',
            IsBillable: supplier?.IsBillable,
            payment_terms: supplier?.payment_terms,
          },
        ],
      };
      updateTransaction({
        ...transaction,
        invoice_id: null,
        json: json,
        status: 'Completed',
        checksum: choice.value,
      });
    }
  };

  return transaction.checksum || transaction.json?.from_category ? (
    <div className='matched-expense-cell' data-testid='invoice-preview-container'>
      {transaction.json?.from_category ? (
        <div className='matched-expense-cell__category'>
          <span className='matched-receipt-preview__thumbnail' aria-hidden='true'><FontAwesomeIcon icon={faFileAlt} /></span>
          <span title={transaction.json?.Name}>{transaction.json?.Name}</span>
        </div>
      ) : (
        <InvoicePreview
          file_name={transaction.json?.Name || `${statement._id}_${transaction.checksum}.pdf`}
          currentInvoice={null}
          compactLabel={receiptLabel}
          data={data}
          data-testid='invoice-preview'
        />
      )}
      {removeExpense && (!transaction.submitted || transaction.json?.invoice_id) && (
        <button
          type='button'
          className='matched-expense-cell__unlink'
          onClick={(event) => { event.stopPropagation(); removeExpense(transaction); }}
          aria-label={data.t.read('cc_transactions.unlink_expense')}
          title={data.t.read('cc_transactions.unlink_expense')}
          data-testid='remove-expense-button'
        >
          <FontAwesomeIcon icon={faUnlink} />
        </button>
      )}
    </div>
  ) : (
    <>
      {(() => {
        const hasProcessing =
          statement.expenses &&
          statement.expenses.length > 0 &&
          statement.expenses.some((expense) => ['Pending', 'Processing'].includes(expense.status));
        // If we previously had a matched receipt, keep showing it instead of spinner while processing new ones
        if (!transaction.checksum && hasProcessing && lastMatchedRef.current) {
          return (
            <div className='d-flex align-items-center justify-content-between' data-testid='invoice-preview-container-updating'>
              <InvoicePreview file_name={lastMatchedRef.current.file_name} compactLabel={lastMatchedRef.current.name || receiptLabel} currentInvoice={null} data={data} data-testid='invoice-preview-updating' />
              <span className='ml-2 small text-muted'>{data.t.read('cc_transactions.updating_receipt') || 'Updating receipt...'}</span>
            </div>
          );
        }
        if (hasProcessing) {
          return <LoadingSpinner data-testid='loading-spinner' />;
        }
        return (
          <Autocomplete
            className='matched-expense-cell__selector'
            data-testid='expense-selector'
            options={expenses.filter((expense) => !matchingExpenses?.includes(expense.checksum))
              .map((expense) => ({ label: expense.original_file_name || expense.name || expense.json?.Name || '—', value: expense.checksum }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(event, choice) => handleChange(choice)}
            value={null}
            onClick={(event) => event.stopPropagation()}
            renderInput={(params) => <TextField {...params} placeholder={data.t.read('cc_transactions.select_expense')} inputProps={{ ...params.inputProps, 'aria-label': data.t.read('cc_transactions.select_expense') }} />}
            size='small'
            // The menu is portaled, so apply its highlight colors through ListboxProps.
            ListboxProps={{ sx: {
              '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option.Mui-focusVisible': {
                backgroundColor: '#fff2dc', color: '#8a4b00',
              },
            } }}
            sx={{
              width: 320,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', fontSize: '14px',
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f39800' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f39800' },
              },
            }}
          />
        );
      })()}
    </>
  );
};

export default inject('data')(InvoicePreviewSelect);

InvoicePreviewSelect.propTypes = {
  data: PropTypes.shape({
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  transaction: PropTypes.shape({
    checksum: PropTypes.string,
    file_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }).isRequired,
  statement: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    expenses: PropTypes.arrayOf(
      PropTypes.shape({
        checksum: PropTypes.string.isRequired,
        status: PropTypes.string,
        json: PropTypes.object,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  updateTransaction: PropTypes.func.isRequired,
  matchingExpenses: PropTypes.arrayOf(PropTypes.string),
  removeExpense: PropTypes.func,
  additionalExpenseOptions: PropTypes.arrayOf(
    PropTypes.shape({
      checksum: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};

InvoicePreviewSelect.defaultProps = {
  matchingExpenses: [],
  removeExpense: undefined,
  additionalExpenseOptions: [],
};
